import React, { FC, useRef } from 'react';
import { COURIER_STATUS, TCourier } from '../../services/api';
import { mergeStrings } from '../../../../shared/utils/index';
import { EBadgeStatus, JetStatusBadge } from '@src/shared/components/JetStatusBadges';
import { useTranslation } from 'react-i18next';
import {
  IconEyeOff,
  IconEyeOn,
  IconMoreHorizontal,
  IconSend,
  IconUserCancelled,
} from '@justeattakeaway/pie-icons-react';
import PIEText from '@src/shared/components/PIEText';
import styles from './CourierCard.module.scss';
import PIECard from '@src/shared/components/PIECard';
import useOCFStatus from '@src/shared/hooks/use-ocf-status';
import PIETag, { ETagsColorType } from '@src/shared/components/PIETag';
import dayjs from 'dayjs';
export interface ICourierSelectedProps {
  hash?: string;
  id?: string;
  text?: string;
  email?: string;
  data?: any;
}
interface ICourierCardProps {
  filterType: 'active' | 'pending';
  courierData: TCourier;
  onCourierSelected: (courier: ICourierSelectedProps, isCourierPending: boolean) => any;
}

const CourierCard: FC<ICourierCardProps> = ({ courierData, onCourierSelected }) => {
  const { t } = useTranslation();
  const cardWidth = useRef<HTMLDivElement | null>(null);
  const badgeWidth = useRef<HTMLDivElement | null>(null);
  const settingsIconWidth = useRef<HTMLDivElement | null>(null);
  const { isOCFEnabled } = useOCFStatus();

  const renderCourierSettingsIcons = (settings: any) => {
    if (!isOCFEnabled) {
      return <></>;
    }
    if (settings?.showUnassignedOrders) {
      return (
        <div className={styles.tagDescription}>
          <IconEyeOn size="m" className={styles.tagDescriptionIcon} color="#fff" />
          <span className={styles.tagDescriptionText}>{t('manage_couriers.ORDERS_VISIBLE')}</span>
        </div>
      );
    } else {
      return (
        <PIEText
          size="var(--xds-font-size-14)"
          color="var(--xds-color-content-default-solid)"
          fontWeight="var(--xds-font-weight-regular)"
          text={
            <div className={styles.tagDescriptionNegative}>
              <IconEyeOff size="s" color="var(--xds-color-content-interactive-dark-solid)" />
              {t('manage_couriers.ORDERS_HIDDEN')}
            </div>
          }
        ></PIEText>
      );
    }
  };

  /* Component Utils */
  const getIsPendingCouriers = (courierStatus: COURIER_STATUS): boolean => {
    if (
      courierStatus === COURIER_STATUS.INVITATION_SENT ||
      courierStatus === COURIER_STATUS.NOT_REGISTERED ||
      courierStatus === COURIER_STATUS.DISCONNECTED
    ) {
      return true;
    }
    return false;
  };

  const isNewCourier = (dateAdded: Date): boolean => {
    const twentyFourHoursAgo = dayjs().subtract(24, 'hour');
    return dayjs(dateAdded).isAfter(twentyFourHoursAgo);
  };

  return (
    <>
      <div className={styles.courierCardContainer} ref={cardWidth}>
        {/* showUnassignedOrders */}
        <PIECard
          onClick={() => {
            onCourierSelected(
              { hash: courierData.hash, id: courierData.id, email: courierData.email },
              getIsPendingCouriers(courierData.status)
            );
          }}
        >
          <div className={styles.courierCardContent}>
            <div
              className={`${styles.courierCardMain} ${
                getIsPendingCouriers(courierData.status) ? styles.courierCardIsPending : ''
              }`}
            >
              {/* get the screen size and re calculate the ellipsis fiield */}

              {getIsPendingCouriers(courierData.status) ? (
                <span ref={badgeWidth}>
                  <JetStatusBadge
                    status={
                      courierData.status === COURIER_STATUS.DISCONNECTED
                        ? EBadgeStatus.DISCONNECT
                        : EBadgeStatus.INVITATION
                    }
                    badgeText={
                      courierData.status === COURIER_STATUS.DISCONNECTED
                        ? t('manage_couriers.DISCONNECTED')
                        : t('manage_couriers.REQUEST_SENT')
                    }
                    textWeigth="regular"
                    iconComponent={
                      courierData.status === COURIER_STATUS.DISCONNECTED ? (
                        <IconUserCancelled size={'xs'} />
                      ) : (
                        <IconSend size={'xs'} />
                      )
                    }
                  ></JetStatusBadge>
                </span>
              ) : null}

              <PIEText
                size="var(--xds-font-size-16)"
                text={
                  <div className={styles.courierCardText}>
                    <span className={styles.courierCardNameWrapper}>
                      {getIsPendingCouriers(courierData.status)
                        ? courierData.email
                        : mergeStrings(courierData.firstName, courierData.lastName)}
                      {!getIsPendingCouriers(courierData.status) &&
                        isNewCourier(courierData.dateAdded) && isOCFEnabled &&  (
                          <PIETag
                            mode={ETagsColorType.Success}
                            size="small"
                            className={styles.newBadge}
                          >
                            {t('manage_couriers.NEW')}
                          </PIETag>
                        )}
                    </span>
                    {renderCourierSettingsIcons(courierData.settings)}
                  </div>
                }
                fontWeight={
                  getIsPendingCouriers(courierData.status)
                    ? 'var(--xds-font-weight-regular)'
                    : 'var(--xds-font-weight-bold)'
                }
              />
            </div>
            <div className={styles.courierCardMore} ref={settingsIconWidth}>
              <IconMoreHorizontal size={'s'} />
            </div>
          </div>
        </PIECard>
      </div>
    </>
  );
};

export default CourierCard;
